/**
 * Deal/Planning preference badge derivation (T12.5.1 C).
 *
 * Pure, framework-free helpers shared by the Deals surface and the Planning list
 * so both render the SAME stacked badge set from the SAME match rules.
 *
 * Two preference systems × two axes × the values each carries:
 *   - household-wide  like  → ★ Liked   (brand + product)
 *   - household-wide  avoid → ⊘ Avoided (brand + product)
 *   - per-product preferred → ★ Preferred  (brand + product)
 *   - per-product substitute→ ◇ Substitute (brand + product)
 *   - per-product exclude   → ⊘ Excluded   (brand + product)
 *   - the shopping list (product-level on-list) → ★ On your list
 *
 * Each control puts its OWN badge on a deal it matches; a deal shows EVERY badge
 * that applies, stacked, in the locked order (tier → axis → system). Working copy
 * + glyphs ride behind a designer pass — the control → badge mapping is the
 * contract.
 *
 * Matching rules (locked):
 *   - brand axis  → exact curated brand-field match only. Flyer deals expose a
 *     brand only when a linked SystemProduct has a curated Brand relation.
 *   - product axis→ by SystemProduct IDENTITY (a resolved offer's `systemProductId`
 *     or a flyer line id), with a whole-word NAME fallback ONLY for flyer deals
 *     whose find-or-create line id diverges. A resolved offer's identity is
 *     settled by its id — no name fallback for it (that was the category-fanning
 *     defect B).
 */

export function norm(s: string | null | undefined): string {
  return (s ?? '').trim().toLowerCase();
}

// Whole-word containment: `needle` occurs in `haystack` with a word boundary
// before and after. Word characters are [a-z0-9'] (apostrophe kept so brand
// names like "kellogg's" stay one word). Avoids a RegExp built from untrusted
// brand text by scanning index-by-index.
export function wholeWordMatch(haystack: string, needle: string): boolean {
  if (needle.length === 0) return false;
  const isWordChar = (ch: string): boolean => /[a-z0-9']/.test(ch);
  let from = 0;
  for (;;) {
    const idx = haystack.indexOf(needle, from);
    if (idx === -1) return false;
    const before = idx === 0 ? '' : haystack[idx - 1]!;
    const afterIdx = idx + needle.length;
    const after = afterIdx >= haystack.length ? '' : haystack[afterIdx]!;
    const boundaryBefore = before === '' || !isWordChar(before);
    const boundaryAfter = after === '' || !isWordChar(after);
    if (boundaryBefore && boundaryAfter) return true;
    from = idx + 1;
  }
}

// True when any name in `names` appears as a WHOLE WORD in `text` (a deal title
// or a line name). A near-miss substring ("Acmetra" vs "Acme", "Almondine" vs
// "Almond") does NOT fire. The names are already normalized; normalize the text.
export function wholeWordNameMatch(
  text: string,
  names: ReadonlySet<string>,
): boolean {
  if (names.size === 0) return false;
  const t = norm(text);
  if (t.length === 0) return false;
  for (const n of names) {
    if (!n) continue;
    if (wholeWordMatch(t, n)) return true;
  }
  return false;
}

type SystemProductCard = {
  title: string;
  systemProductId?: string | null;
  lines?: ReadonlyArray<{ system_product_id: string; name?: string }>;
};

// True when the card matches one of the given SystemProducts by IDENTITY, with a
// scoped name fallback ONLY for flyer deals whose find-or-create line id diverges.
// A RESOLVED offer (carries `systemProductId`) is decided by its id alone — no
// name fallback (defect B: a liked "Milk" name-matched every milk offer).
export function systemProductMatch(
  card: SystemProductCard,
  ids: ReadonlySet<string>,
  names: ReadonlySet<string>,
): boolean {
  const lines = card.lines ?? [];
  if (ids.size > 0) {
    if (card.systemProductId != null && ids.has(card.systemProductId)) return true;
    if (lines.some((l) => ids.has(l.system_product_id))) return true;
  }
  if (card.systemProductId != null) return false;
  if (names.size > 0) {
    if (wholeWordNameMatch(card.title, names)) return true;
    if (lines.some((l) => wholeWordNameMatch(l.name ?? '', names))) return true;
  }
  return false;
}

// True when the card's curated product-brand field matches any name in `names`.
// Never infer a preference from title text: an unlinked flyer deal has no honest
// brand relation yet.
export function brandMatch(
  card: { title: string; brand: string | null },
  names: ReadonlySet<string>,
): boolean {
  if (names.size === 0) return false;
  const brand = norm(card.brand);
  return brand.length > 0 && names.has(brand);
}

export type BadgeKind =
  | 'on-list'
  | 'preferred-product'
  | 'liked-product'
  | 'preferred-brand'
  | 'liked-brand'
  | 'substitute-product'
  | 'substitute-brand'
  | 'excluded-product'
  | 'avoided-product'
  | 'excluded-brand'
  | 'avoided-brand';

export interface DealBadge {
  kind: BadgeKind;
  label: string;
  tone: 'positive' | 'substitute' | 'negative';
  testidSuffix: string;
}

// Working-copy labels (designer pass owns the final copy/icons/colours). The
// control → badge mapping is the contract; these strings ride behind it.
const BADGE_META: Record<
  BadgeKind,
  { label: string; tone: DealBadge['tone']; testidSuffix: string }
> = {
  'on-list': { label: '★ ON YOUR LIST', tone: 'positive', testidSuffix: 'onlist' },
  'preferred-product': { label: '★ PREFERRED PRODUCT', tone: 'positive', testidSuffix: 'preferred-product' },
  'liked-product': { label: '★ LIKED PRODUCT', tone: 'positive', testidSuffix: 'liked-product' },
  'preferred-brand': { label: '★ PREFERRED BRAND', tone: 'positive', testidSuffix: 'preferred-brand' },
  'liked-brand': { label: '★ LIKED BRAND', tone: 'positive', testidSuffix: 'liked-brand' },
  'substitute-product': { label: '◇ SUBSTITUTE PRODUCT', tone: 'substitute', testidSuffix: 'substitute-product' },
  'substitute-brand': { label: '◇ SUBSTITUTE BRAND', tone: 'substitute', testidSuffix: 'substitute-brand' },
  'excluded-product': { label: '⊘ EXCLUDED PRODUCT', tone: 'negative', testidSuffix: 'excluded-product' },
  'avoided-product': { label: '⊘ AVOIDED PRODUCT', tone: 'negative', testidSuffix: 'avoided-product' },
  'excluded-brand': { label: '⊘ EXCLUDED BRAND', tone: 'negative', testidSuffix: 'excluded-brand' },
  'avoided-brand': { label: '⊘ AVOIDED BRAND', tone: 'negative', testidSuffix: 'avoided-brand' },
};

// The locked stack order (tier → axis → system): most-wanted first, caveats
// last. Tiers: on-list → positive → substitute → negative. Within a tier, product
// axis before brand; per-product (specific) before household-wide (coarse).
export const BADGE_ORDER: BadgeKind[] = [
  'on-list',
  'preferred-product',
  'liked-product',
  'preferred-brand',
  'liked-brand',
  'substitute-product',
  'substitute-brand',
  'excluded-product',
  'avoided-product',
  'excluded-brand',
  'avoided-brand',
];

// Name-set + id-set inputs partitioned by axis × system × value. Each set holds
// the normalized brand NAMES (brand axis) or SystemProduct ids + names (product
// axis) for one badge state. Empty sets simply never fire.
export interface BadgeInputs {
  onList: boolean; // product-level on-list, resolved by the caller
  // household-wide
  likedBrandNames: ReadonlySet<string>;
  avoidedBrandNames: ReadonlySet<string>;
  likedProductIds: ReadonlySet<string>;
  likedProductNames: ReadonlySet<string>;
  avoidedProductIds: ReadonlySet<string>;
  avoidedProductNames: ReadonlySet<string>;
  // per-product (catalog)
  preferredBrandNames: ReadonlySet<string>;
  substituteBrandNames: ReadonlySet<string>;
  excludedBrandNames: ReadonlySet<string>;
  preferredProductIds: ReadonlySet<string>;
  preferredProductNames: ReadonlySet<string>;
  substituteProductIds: ReadonlySet<string>;
  substituteProductNames: ReadonlySet<string>;
  excludedProductIds: ReadonlySet<string>;
  excludedProductNames: ReadonlySet<string>;
}

export type BadgeCard = {
  title: string;
  brand: string | null;
  systemProductId?: string | null;
  lines?: ReadonlyArray<{ system_product_id: string; name?: string }>;
};

// The full stacked badge set for a card, in the locked order. Every control that
// matches contributes its own badge; a like/avoid clash on the same card simply
// shows both (no precedence puzzle).
export function deriveBadges(card: BadgeCard, inputs: BadgeInputs): DealBadge[] {
  const present = new Set<BadgeKind>();
  if (inputs.onList) present.add('on-list');
  // Product axis (identity + flyer name fallback).
  if (systemProductMatch(card, inputs.preferredProductIds, inputs.preferredProductNames))
    present.add('preferred-product');
  if (systemProductMatch(card, inputs.likedProductIds, inputs.likedProductNames))
    present.add('liked-product');
  if (systemProductMatch(card, inputs.substituteProductIds, inputs.substituteProductNames))
    present.add('substitute-product');
  if (systemProductMatch(card, inputs.excludedProductIds, inputs.excludedProductNames))
    present.add('excluded-product');
  if (systemProductMatch(card, inputs.avoidedProductIds, inputs.avoidedProductNames))
    present.add('avoided-product');
  // Brand axis (exact curated Brand field).
  if (brandMatch(card, inputs.preferredBrandNames)) present.add('preferred-brand');
  if (brandMatch(card, inputs.likedBrandNames)) present.add('liked-brand');
  if (brandMatch(card, inputs.substituteBrandNames)) present.add('substitute-brand');
  if (brandMatch(card, inputs.excludedBrandNames)) present.add('excluded-brand');
  if (brandMatch(card, inputs.avoidedBrandNames)) present.add('avoided-brand');

  return BADGE_ORDER.filter((k) => present.has(k)).map((kind) => ({
    kind,
    label: BADGE_META[kind].label,
    tone: BADGE_META[kind].tone,
    testidSuffix: BADGE_META[kind].testidSuffix,
  }));
}

// Empty inputs (a neutral household): every set empty, nothing on-list. Useful
// as a base to spread over.
export const EMPTY_BADGE_INPUTS: BadgeInputs = {
  onList: false,
  likedBrandNames: new Set(),
  avoidedBrandNames: new Set(),
  likedProductIds: new Set(),
  likedProductNames: new Set(),
  avoidedProductIds: new Set(),
  avoidedProductNames: new Set(),
  preferredBrandNames: new Set(),
  substituteBrandNames: new Set(),
  excludedBrandNames: new Set(),
  preferredProductIds: new Set(),
  preferredProductNames: new Set(),
  substituteProductIds: new Set(),
  substituteProductNames: new Set(),
  excludedProductIds: new Set(),
  excludedProductNames: new Set(),
};
